import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useScroll, useTransform } from 'framer-motion';
import api from '../lib/api';
import {
  LuArrowRight,
  LuMinus,
  LuPlus,
  LuChevronDown,
  LuCheck,
  LuSmartphone,
  LuShieldCheck,
  LuUser,
  LuGlobe,
  LuTrendingUp,
  LuTriangleAlert,
  LuX,
} from 'react-icons/lu';
import { Icon, InstagramIcon, ThreadsIcon } from '../lib/icons';
import PhoneMockup from '../components/PhoneMockup';
import styles from './Home.module.css';

// Simplified flat pricing: $2.50/account, $2.00 for 30+ accounts
const pricingTiers = [
  { min: 1, max: 29, price: 2.50 },
  { min: 30, max: Infinity, price: 2.00 },
];

function getUnitPrice(qty: number): number {
  const tier = pricingTiers.find(t => qty >= t.min && qty <= t.max);
  return tier ? tier.price : pricingTiers[0].price;
}

// Luxury easing curve for framer-motion
const luxuryEase = [0.4, 0, 0, 1] as const;
const softEase = [0.22, 1, 0.36, 1] as const;

// Partners section component
interface Partner {
  id: string;
  name: string;
  logoUrl: string;
  redirectUrl: string | null;
}

const PartnersSection: React.FC = () => {
  const { t } = useTranslation();
  const [partners, setPartners] = useState<Partner[]>([]);

  useEffect(() => {
    api.get('/partners')
      .then((res) => setPartners(res.data.partners || []))
      .catch((err) => console.error('Failed to fetch partners:', err));
  }, []);

  if (partners.length === 0) return null;

  return (
    <section className={styles.partners} id="partners">
      <div className={styles.sectionInner}>
        <motion.div
          className={styles.sectionHead}
          style={{ textAlign: 'center' }}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.8, ease: luxuryEase }}
        >
          <h2>{t('partners.title' as string) || 'Ils nous font confiance'}</h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', maxWidth: '600px', margin: '12px auto 0' }}>
            {t('partners.subtitle' as string) || 'Plus de 100 agences et professionnels utilisent nos services'}
          </p>
        </motion.div>

        <div className={styles.partnersGrid}>
          {partners.map((partner, i) => (
            <motion.div
              key={partner.id}
              className={styles.partnerCard}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1, ease: softEase }}
            >
              {partner.redirectUrl ? (
                <a href={partner.redirectUrl} target="_blank" rel="noopener noreferrer" className={styles.partnerLink}>
                  <img src={partner.logoUrl} alt={partner.name} />
                  <span className={styles.partnerName}>{partner.name}</span>
                </a>
              ) : (
                <>
                  <img src={partner.logoUrl} alt={partner.name} />
                  <span className={styles.partnerName}>{partner.name}</span>
                </>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Home: React.FC = () => {
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState(1);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const unitPrice = getUnitPrice(quantity);
  const totalPrice = unitPrice * quantity;

  // Parallax refs
  const heroRef = useRef<HTMLElement>(null);
  const processRef = useRef<HTMLElement>(null);

  // Hero parallax
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const heroTitleY = useTransform(heroProgress, [0, 1], ['0%', '20%']);
  const heroOpacity = useTransform(heroProgress, [0, 0.5], [1, 0]);
  const heroBgScale = useTransform(heroProgress, [0, 1], [1, 1.12]);

  // Process parallax
  const { scrollYProgress: processProgress } = useScroll({
    target: processRef,
    offset: ['start end', 'end start'],
  });
  const processLineH = useTransform(processProgress, [0.1, 0.7], ['0%', '100%']);

  const faqItems = [
    { q: t('faq.q1'), a: t('faq.a1') },
    { q: t('faq.q2'), a: t('faq.a2') },
    { q: t('faq.q3'), a: t('faq.a3') },
    { q: t('faq.q4'), a: t('faq.a4') },
    { q: t('faq.q5'), a: t('faq.a5') },
    { q: t('faq.q6'), a: t('faq.a6') },
  ];
  const q7 = t('faq.q7', { defaultValue: '' });
  const a7 = t('faq.a7', { defaultValue: '' });
  const q8 = t('faq.q8', { defaultValue: '' });
  const a8 = t('faq.a8', { defaultValue: '' });
  if (q7) faqItems.push({ q: q7, a: a7 });
  if (q8) faqItems.push({ q: q8, a: a8 });

  const handleQuantityChange = (val: number) => {
    if (val >= 1 && val <= 100) setQuantity(val);
  };

  return (
    <div className={styles.page}>
      {/* ======== HERO — full viewport, parallax ======== */}
      <section className={styles.hero} ref={heroRef} id="hero">
        {/* Parallax background glow */}
        <motion.div className={styles.heroBgGlow} style={{ scale: heroBgScale }} />

        <motion.div
          className={styles.heroContent}
          style={{ y: heroTitleY, opacity: heroOpacity }}
        >
          <motion.span
            className={styles.heroLabel}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: luxuryEase }}
          >
            <span className={styles.heroLabelDot} />
            {t('hero.badge')}
            <span className={styles.heroLabelDot} />
          </motion.span>

          <motion.h1
            className={styles.heroTitle}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2, ease: luxuryEase }}
          >
            {t('hero.title')}
          </motion.h1>

          <motion.p
            className={styles.heroSub}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease: luxuryEase }}
          >
            {t('hero.subtitle')}
          </motion.p>

          <motion.div
            className={styles.heroCtas}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5, ease: luxuryEase }}
          >
            <Link to="/checkout" className={styles.ctaPrimary}>
              {t('hero.cta')}
              <Icon icon={LuArrowRight} size={15} strokeWidth={2.5} />
            </Link>
            <button
              className={styles.ctaGhost}
              onClick={() => {
                const el = document.getElementById('process');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {t('hero.ctaSecondary')}
            </button>
          </motion.div>

          <motion.div
            className={styles.heroMeta}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.7, ease: luxuryEase }}
          >
            <span>{t('hero.trust2')}</span>
            <span className={styles.dot} />
            <span>{t('hero.trust3')}</span>
            <span className={styles.dot} />
            <span>{t('hero.trust4')}</span>
          </motion.div>
        </motion.div>

        {/* Hero phone mockup — floating on the right with orbiting badges */}
        <motion.div
          className={styles.heroPhone}
          initial={{ opacity: 0, y: 40, rotate: 3 }}
          animate={{ opacity: 1, y: 0, rotate: 3 }}
          transition={{ duration: 1, delay: 0.4, ease: luxuryEase }}
          style={{ y: useTransform(heroProgress, [0, 1], ['0%', '15%']) }}
        >
          <PhoneMockup
            image="/showcase/model-2.jpg"
            avatar="/showcase/model-1.jpg"
            username="eva_rose"
            followers="287K"
            posts="143"
            isReel
            reelVideo="/showcase/reel.mp4"
            views="1.8M"
            scale={0.85}
          />

          {/* Floating badges around the phone */}
          <motion.span
            className={`${styles.floatingBadge} ${styles.fb1}`}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 1.0, ease: softEase }}
          >
            <Icon icon={LuSmartphone} size={11} strokeWidth={2} />
            Real iPhone + 4G
          </motion.span>
          <motion.span
            className={`${styles.floatingBadge} ${styles.fb2}`}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 1.2, ease: softEase }}
          >
            <Icon icon={LuShieldCheck} size={11} strokeWidth={2} />
            Latest iOS
          </motion.span>
          <motion.span
            className={`${styles.floatingBadge} ${styles.fb3}`}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 1.4, ease: softEase }}
          >
            <Icon icon={LuUser} size={11} strokeWidth={2} />
            Humanized creation
          </motion.span>
          <motion.span
            className={`${styles.floatingBadge} ${styles.fb4}`}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 1.6, ease: softEase }}
          >
            <Icon icon={LuGlobe} size={11} strokeWidth={2} />
            FR &amp; US available
          </motion.span>
          <motion.span
            className={`${styles.floatingBadge} ${styles.fb5}`}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 1.8, ease: softEase }}
          >
            <Icon icon={LuTrendingUp} size={11} strokeWidth={2} />
            Built for scaling
          </motion.span>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          className={styles.scrollHint}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
        >
          <div className={styles.scrollLine} />
        </motion.div>
      </section>

      {/* ======== PLATFORMS — Instagram & Threads ======== */}
      <section className={styles.platforms}>
        <motion.div
          className={styles.platformsContainer}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.9, ease: luxuryEase }}
        >
          <div className={styles.platformsBox}>
            <motion.div
              className={styles.platformsHeader}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2, ease: luxuryEase }}
            >
              <h3>{t('platforms.title')}</h3>
              <p>{t('platforms.description')}</p>
            </motion.div>

            <motion.div
              className={styles.platformsLogos}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4, ease: luxuryEase }}
            >
              <motion.div
                className={styles.platformBadge}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.5, ease: softEase }}
              >
                <InstagramIcon size={32} className={styles.platformIcon} />
                <span>Instagram</span>
              </motion.div>

              <motion.div
                className={styles.platformDivider}
                initial={{ opacity: 0, scaleX: 0 }}
                whileInView={{ opacity: 1, scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.65, ease: softEase }}
              />

              <motion.div
                className={styles.platformBadge}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.7, ease: softEase }}
              >
                <ThreadsIcon size={32} className={styles.platformIcon} />
                <span>Threads</span>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* ======== SHOWCASE — floating phones with Instagram mockups ======== */}
      <section className={styles.showcase}>
        <div className={styles.showcaseInner}>
          <motion.div
            className={styles.showcaseText}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, ease: luxuryEase }}
          >
            <span className={styles.sectionLabel}>{t('hero.trust1')}</span>
            <h2>{t('hero.trust1')}</h2>
            <p className={styles.sectionDesc}>
              {t('rigor.subtitle')}
            </p>
          </motion.div>

          <div className={styles.phonesRow}>
            <motion.div
              className={styles.phoneFloat}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0, ease: luxuryEase }}
            >
              <PhoneMockup
                image="/showcase/model-5.jpg"
                avatar="/showcase/model-1.jpg"
                gridImages={[
                  '/showcase/model-5.jpg', '/showcase/model-1.jpg', '/showcase/model-8.jpg',
                  '/showcase/model-11.jpg', '/showcase/model-2.jpg', '/showcase/model-9.jpg',
                  '/showcase/model-13.jpg', '/showcase/model-3.jpg', '/showcase/model-12.jpg',
                ]}
                username="bella_vibes"
                followers="324K"
                posts="186"
              />
            </motion.div>

            <motion.div
              className={`${styles.phoneFloat} ${styles.phoneCenter}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15, ease: luxuryEase }}
            >
              <PhoneMockup
                image="/showcase/model-3.jpg"
                avatar="/showcase/model-10.jpg"
                username="luna_beach"
                followers="512K"
                posts="234"
                isReel
                reelVideo="/showcase/reel.mp4"
                views="2.4M"
              />
            </motion.div>

            <motion.div
              className={styles.phoneFloat}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.3, ease: luxuryEase }}
            >
              <PhoneMockup
                image="/showcase/model-4.jpg"
                avatar="/showcase/model-6.jpg"
                gridImages={[
                  '/showcase/model-4.jpg', '/showcase/model-6.jpg', '/showcase/model-10.jpg',
                  '/showcase/model-7.jpg', '/showcase/model-12.jpg', '/showcase/model-8.jpg',
                  '/showcase/model-9.jpg', '/showcase/model-11.jpg', '/showcase/model-13.jpg',
                ]}
                username="fit_maya"
                followers="198K"
                posts="97"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ======== PROCESS — numbered timeline with scroll progress ======== */}
      <section className={styles.process} ref={processRef} id="process">
        <div className={styles.sectionInner}>
          <motion.div
            className={styles.sectionHead}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8, ease: luxuryEase }}
          >
            <span className={styles.sectionLabel}>{t('process.title').split('.')[0]}</span>
            <h2>{t('process.title')}</h2>
          </motion.div>

          <div className={styles.timeline}>
            {/* Animated progress line */}
            <div className={styles.timelineTrack}>
              <motion.div className={styles.timelineFill} style={{ height: processLineH }} />
            </div>

            {[
              { num: '01', title: t('process.step1title'), desc: t('process.step1desc') },
              { num: '02', title: t('process.step2title'), desc: t('process.step2desc') },
              { num: '03', title: t('process.step3title'), desc: t('process.step3desc') },
              { num: '04', title: t('process.step4title'), desc: t('process.step4desc') },
            ].map((step, i) => (
              <motion.div
                key={i}
                className={styles.timelineStep}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: luxuryEase }}
              >
                <div className={styles.stepMarker}>
                  <span>{step.num}</span>
                </div>
                <div className={styles.stepContent}>
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ======== RIGOR — bento with stagger reveal ======== */}
      <section className={styles.rigor} id="rigor">
        <div className={styles.sectionInner}>
          <motion.div
            className={styles.sectionHead}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8, ease: luxuryEase }}
          >
            <span className={styles.sectionLabel}>{t('rigor.title')}</span>
            <h2>{t('rigor.title')}</h2>
            <p className={styles.sectionDesc}>{t('rigor.subtitle')}</p>
          </motion.div>

          <div className={styles.bento}>
            {[
              { title: t('rigor.item1title'), desc: t('rigor.item1desc'), accent: true },
              { title: t('rigor.item2title'), desc: t('rigor.item2desc'), accent: false },
              { title: t('rigor.item3title'), desc: t('rigor.item3desc'), accent: false },
              { title: t('rigor.item4title'), desc: t('rigor.item4desc'), accent: true },
            ].map((item, i) => (
              <motion.div
                key={i}
                className={`${styles.bentoItem} ${item.accent ? styles.bentoAccent : ''}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: luxuryEase }}
              >
                <span className={styles.bentoNum}>{String(i + 1).padStart(2, '0')}</span>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ======== COMPARISON — Us vs. Them ======== */}
      <section className={styles.compare} id="compare">
        <div className={styles.sectionInner}>
          <motion.div
            className={styles.sectionHead}
            style={{ textAlign: 'center' }}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8, ease: luxuryEase }}
          >
            <span className={styles.sectionLabel} style={{ justifyContent: 'center' }}>{t('compare.title')}</span>
            <h2>{t('compare.title')}</h2>
            <p className={styles.sectionDesc} style={{ margin: '14px auto 0', textAlign: 'center' }}>{t('compare.subtitle')}</p>
          </motion.div>

          <motion.div
            className={styles.compareTable}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7, ease: luxuryEase }}
          >
            {/* Desktop: Side-by-side comparison */}
            <div className={styles.compareDesktop}>
              {/* Header row */}
              <div className={styles.compareHeader}>
                <div className={styles.compareColOthers}>
                  <span>{t('compare.othersLabel')}</span>
                </div>
                <div className={styles.compareColUs}>
                  <span>{t('compare.usLabel')}</span>
                </div>
              </div>

              {/* Comparison rows */}
              {[1, 2, 3, 4, 5, 6].map((row, i) => (
                <motion.div
                  key={row}
                  className={styles.compareRow}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06, ease: softEase }}
                >
                  <div className={styles.compareCellOthers}>
                    <Icon icon={LuX} size={14} strokeWidth={2.5} className={styles.compareIconBad} />
                    <span>{t(`compare.row${row}others`)}</span>
                  </div>
                  <div className={styles.compareCellUs}>
                    <Icon icon={LuCheck} size={14} strokeWidth={2.5} className={styles.compareIconGood} />
                    <span>{t(`compare.row${row}us`)}</span>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Mobile: Stacked cards */}
            <div className={styles.compareMobile}>
              {/* Others section */}
              <motion.div
                className={styles.compareMobileCard}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, ease: luxuryEase }}
              >
                <div className={styles.compareMobileHeader + ' ' + styles.compareMobileHeaderBad}>
                  <Icon icon={LuX} size={16} strokeWidth={2.5} className={styles.compareIconBad} />
                  <span>{t('compare.othersLabel')}</span>
                </div>
                <div className={styles.compareMobileList}>
                  {[1, 2, 3, 4, 5, 6].map((row) => (
                    <div key={row} className={styles.compareMobileItem}>
                      <Icon icon={LuX} size={12} strokeWidth={2.5} className={styles.compareIconBad} />
                      <span>{t(`compare.row${row}others`)}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Us section */}
              <motion.div
                className={styles.compareMobileCard}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.15, ease: luxuryEase }}
              >
                <div className={styles.compareMobileHeader + ' ' + styles.compareMobileHeaderGood}>
                  <Icon icon={LuCheck} size={16} strokeWidth={2.5} className={styles.compareIconGood} />
                  <span>{t('compare.usLabel')}</span>
                </div>
                <div className={styles.compareMobileList}>
                  {[1, 2, 3, 4, 5, 6].map((row) => (
                    <div key={row} className={styles.compareMobileItem}>
                      <Icon icon={LuCheck} size={12} strokeWidth={2.5} className={styles.compareIconGood} />
                      <span>{t(`compare.row${row}us`)}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ======== PRICING ======== */}
      <section className={styles.pricing} id="pricing">
        <div className={styles.pricingWrap}>
          <motion.div
            className={styles.sectionHead}
            style={{ textAlign: 'center' }}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8, ease: luxuryEase }}
          >
            <span className={styles.sectionLabel} style={{ justifyContent: 'center' }}>{t('pricing.title')}</span>
            <h2>{t('pricing.title')}</h2>
          </motion.div>

          <motion.div
            className={styles.calc}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7, ease: luxuryEase }}
          >
            {/* Scarcity badge */}
            <div className={styles.scarcityBadge}>
              <Icon icon={LuTriangleAlert} size={12} strokeWidth={2} />
              <span>{t('pricing.scarcity')}</span>
            </div>

            {/* Bulk deal badge - shown when 30+ accounts */}
            {quantity >= 30 && (
              <motion.div 
                className={styles.bulkDealBadge}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: luxuryEase }}
              >
                <Icon icon={LuCheck} size={14} strokeWidth={2.5} />
                <span>{t('pricing.bulkDeal')} — {t('pricing.savePerAccount')}</span>
              </motion.div>
            )}

            {/* Quick select buttons */}
            <div className={styles.quickSelectSection}>
              <label className={styles.fieldLabel}>{t('pricing.quickSelect')}</label>
              <div className={styles.quickSelectBtns}>
                {[1, 10, 30].map((val) => (
                  <button
                    key={val}
                    className={`${styles.quickSelectBtn} ${quantity === val ? styles.quickSelectActive : ''}`}
                    onClick={() => handleQuantityChange(val)}
                  >
                    {val} {val === 1 ? t('pricing.account') : t('pricing.accounts')}
                    {val === 30 && <span className={styles.quickSelectBest}>-20%</span>}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div className={styles.qtySection}>
              <label className={styles.fieldLabel}>{t('checkout.quantityLabel')}</label>
              <div className={styles.qtyWrap}>
                <button className={styles.qtyBtn} onClick={() => handleQuantityChange(quantity - 1)} disabled={quantity <= 1}>
                  <Icon icon={LuMinus} size={15} strokeWidth={2} />
                </button>
                <input
                  type="number" className={styles.qtyInput} value={quantity}
                  onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                  min={1} max={100}
                />
                <button className={styles.qtyBtn} onClick={() => handleQuantityChange(quantity + 1)} disabled={quantity >= 100}>
                  <Icon icon={LuPlus} size={15} strokeWidth={2} />
                </button>
              </div>
              <div className={styles.tierRow}>
                {pricingTiers.map((tier, i) => (
                  <span key={i} className={`${styles.tierChip} ${quantity >= tier.min && quantity <= tier.max ? styles.tierOn : ''}`}>
                    {tier.max === Infinity ? `${tier.min}+` : `${tier.min}-${tier.max}`}
                    <strong>${tier.price.toFixed(2)}</strong>
                  </span>
                ))}
              </div>
            </div>

            {/* Price — unit price and total */}
            <div className={styles.priceBox}>
              <div className={styles.priceCol}>
                <span className={styles.priceLabel}>{t('pricing.unitPrice')}</span>
                <span className={styles.priceNum}>
                  ${unitPrice.toFixed(2)}
                  <span className={styles.pricePerAccount}>/{t('pricing.account')}</span>
                </span>
              </div>
              <div className={styles.priceDivider} />
              <div className={styles.priceCol}>
                <span className={styles.priceLabel}>{t('pricing.total')}</span>
                <span className={`${styles.priceNum} ${styles.priceHighlight}`}>${totalPrice.toFixed(2)}</span>
              </div>
            </div>

            {/* Features */}
            <div className={styles.featureList}>
              {['manual', 'proxy', 'verified', 'profile', 'email', 'warranty'].map((key) => (
                <div key={key} className={styles.featureItem}>
                  <Icon icon={LuCheck} size={13} strokeWidth={2.5} />
                  <span>{t(`pricing.features.${key}`)}</span>
                </div>
              ))}
            </div>

            <Link
              to={`/checkout?qty=${quantity}`}
              className={styles.calcCta}
            >
              {t('pricing.order')}
              <Icon icon={LuArrowRight} size={15} strokeWidth={2.5} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ======== FAQ ======== */}
      <section className={styles.faq} id="faq">
        <div className={styles.faqWrap}>
          <motion.div
            className={styles.sectionHead}
            style={{ textAlign: 'center' }}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8, ease: luxuryEase }}
          >
            <h2>{t('faq.title')}</h2>
          </motion.div>

          <div className={styles.faqList}>
            {faqItems.map((item, i) => (
              <motion.div
                key={i}
                className={`${styles.faqItem} ${openFaq === i ? styles.faqOpen : ''}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.04, ease: softEase }}
              >
                <button className={styles.faqQ} onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{item.q}</span>
                  <Icon icon={LuChevronDown} size={16} strokeWidth={2} className={`${styles.faqIcon} ${openFaq === i ? styles.faqIconOpen : ''}`} />
                </button>
                <motion.div
                  className={styles.faqA}
                  initial={false}
                  animate={{
                    height: openFaq === i ? 'auto' : 0,
                    opacity: openFaq === i ? 1 : 0
                  }}
                  transition={{ duration: 0.35, ease: luxuryEase }}
                  style={{ overflow: 'hidden' }}
                >
                  <div className={styles.faqAInner}>{item.a}</div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ======== SECURITY — minimal, text-driven ======== */}
      <section className={styles.security} id="security">
        <div className={styles.sectionInner}>
          <motion.div
            className={styles.sectionHead}
            style={{ textAlign: 'center' }}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8, ease: luxuryEase }}
          >
            <h2>{t('security.title')}</h2>
          </motion.div>

          <div className={styles.secGrid}>
            {[
              { title: t('security.item1'), desc: t('security.item1desc') },
              { title: t('security.item2'), desc: t('security.item2desc') },
              { title: t('security.item3'), desc: t('security.item3desc') },
            ].map((item, i) => (
              <motion.div
                key={i}
                className={styles.secItem}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: luxuryEase }}
              >
                <span className={styles.secNum}>{String(i + 1).padStart(2, '0')}</span>
                <h4>{item.title}</h4>
                <p>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ======== PARTNERS — logo showcase ======== */}
      <PartnersSection />

      {/* ======== CONTACT — minimal band ======== */}
      <section className={styles.contact} id="contact">
        <div className={styles.contactInner}>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: luxuryEase }}
          >
            {t('contact.subtitle')}
          </motion.h2>
          <motion.div
            className={styles.contactLinks}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15, ease: luxuryEase }}
          >
            <a href="https://t.me/ofmigshopsupport" target="_blank" rel="noopener noreferrer">Telegram: @ofmigshopsupport</a>
          </motion.div>
          <motion.p
            className={styles.contactNote}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {t('contact.response')}
          </motion.p>
        </div>
      </section>
    </div>
  );
};

export default Home;
